import {
  ETextStyleVariant,
  GeneralCaretUpIcon,
  Popover,
  RawButton,
  Text,
  TPopoverTriggerProps,
} from '@outdoorsyco/bonfire';
import cn from 'classnames';
import throttle from 'lodash/throttle';
import React, { forwardRef, useEffect, useRef, useState } from 'react';

import OutdoorsyLogo from '@/assets/images/outdoorsy-logo.svg';
import Link from '@/components/switchback/Link/Link';

import { TSeasonalGuideSection } from './types';

type TSeasonalGuideSectionsNavProps = {
  sections: TSeasonalGuideSection[];
  activeSectionId?: string;
  onScrollToSection: (sectionId: string) => void;
};

export const SeasonalGuideSectionsNav = ({
  sections,
  activeSectionId,
  onScrollToSection,
}: TSeasonalGuideSectionsNavProps) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);

  useEffect(() => {
    const determineIfSticky = throttle(
      () => {
        const rootRect = rootRef.current?.getBoundingClientRect();
        setIsSticky(rootRect ? Math.round(rootRect.top) <= 0 : false);
      },
      100,
      {
        leading: true,
        trailing: true,
      },
    );

    determineIfSticky();

    window.addEventListener('scroll', determineIfSticky, { passive: true });
    window.addEventListener('resize', determineIfSticky, { passive: true });

    return () => {
      window.removeEventListener('scroll', determineIfSticky);
      window.removeEventListener('resize', determineIfSticky);
    };
  }, []);

  return (
    <div ref={rootRef} className="sticky top-0 h-[72px] z-1299 bg-canvas-100">
      <div className="items-center hidden h-full mx-4 lg:flex xxxl:mx-16">
        <div className={cn('justify-start hidden w-[200px]', { 'xl:flex': isSticky })}>
          <Link href="/">
            <OutdoorsyLogo className="text-gray-900 translate-y-1 transform" />
          </Link>
        </div>

        <div className="relative flex justify-center flex-grow gap-4">
          {sections.map((section, index) => (
            <a
              key={index}
              href={`#${section.id}`}
              onClick={e => {
                e.preventDefault();
                onScrollToSection(section.id);
              }}
              className={cn('px-6 pb-2', {
                'border-b-2 border-gray-900': activeSectionId === section.id,
              })}>
              <Text variant={ETextStyleVariant.MediumBold}>{section.tag}</Text>
            </a>
          ))}
        </div>

        <div className={cn('hidden justify-end w-[200px]', { 'xl:flex': isSticky })}>
          <button
            onClick={e => {
              e.preventDefault();
              window.scrollTo({
                top: 0,
                behavior: 'smooth',
              });
            }}
            className="flex items-center gap-2">
            <Text variant={ETextStyleVariant.MediumRegular}>Back to top</Text>
            <GeneralCaretUpIcon width={24} height={24} />
          </button>
        </div>
      </div>

      <div
        className={cn('flex items-center h-full mx-4 lg:hidden', {
          'justify-between': isSticky,
          'justify-end': !isSticky,
        })}>
        <Link href="/" className={cn({ hidden: !isSticky })}>
          <OutdoorsyLogo className="text-gray-900 translate-y-1 transform" />
        </Link>
        <Popover
          menuOnly
          components={{
            // eslint-disable-next-line react/display-name
            Trigger: forwardRef<HTMLButtonElement, TPopoverTriggerProps>(
              ({ className: _className, ...props }, ref) => {
                return (
                  <RawButton ref={ref} {...props} className="flex items-center gap-2">
                    <Text variant={ETextStyleVariant.MediumBold}>
                      {sections.find(section => section.id === activeSectionId)?.tag}
                    </Text>

                    <GeneralCaretUpIcon
                      width={24}
                      height={24}
                      className={cn('transition-transform', {
                        'rotate-180': !props['aria-expanded'],
                      })}
                    />
                  </RawButton>
                );
              },
            ),
          }}>
          {sections
            .filter(section => activeSectionId !== section.id)
            .map((section, index) => {
              return (
                <Popover.Option
                  key={index}
                  value={section.id}
                  label={section.tag}
                  onClick={() => {
                    onScrollToSection(section.id);
                  }}
                />
              );
            })}
        </Popover>
      </div>
    </div>
  );
};
